import type { AnalyticsConfig } from "../shared/types";
import { isNativeApp } from './platform';

type AnalyticsValue = string | number | boolean | undefined;
type AnalyticsParams = Record<string, AnalyticsValue>;
type GtagCommand =
  | ["js", Date]
  | ["config", string, AnalyticsParams?]
  | ["event", string, AnalyticsParams];
type Gtag = (...command: GtagCommand) => void;
type DataLayerEntry = GtagCommand | IArguments | Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerEntry[];
    gtag?: Gtag;
  }
}

let activeMeasurementId = "";
let suppressNextPageView = false;

export function isGa4MeasurementId(value: string | undefined): boolean {
  return /^G-[A-Z0-9]{4,}$/i.test((value || "").trim());
}

export function googleAnalyticsScriptUrl(measurementId: string): string {
  return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId.trim())}`;
}

export function initializeGoogleAnalytics(config: AnalyticsConfig | undefined): boolean {
  if (isNativeApp()) return false;
  if (!config?.enabled || !isGa4MeasurementId(config.measurementId)) return false;
  if (typeof window === "undefined" || typeof document === "undefined") return false;

  const measurementId = config.measurementId.trim();
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || gtagShim;
  const pageTagAlreadyConfigured = hasConfigCommand(measurementId);

  if (!hasGoogleAnalyticsScript(measurementId)) {
    const script = document.createElement("script");
    script.async = true;
    script.id = "stone-harbor-ga4";
    script.src = googleAnalyticsScriptUrl(measurementId);
    document.head.appendChild(script);
  }

  activeMeasurementId = measurementId;
  if (pageTagAlreadyConfigured) {
    suppressNextPageView = true;
    return true;
  }

  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    app_name: "stone_harbor_beach_app",
    send_page_view: false
  });
  return true;
}

export function trackPageView(path: string, title: string): void {
  if (suppressNextPageView) {
    suppressNextPageView = false;
    return;
  }

  trackAnalyticsEvent("page_view", {
    page_path: path,
    page_title: title
  });
}

export function trackAnalyticsEvent(name: string, params: AnalyticsParams = {}): void {
  if (!activeMeasurementId || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, cleanAnalyticsParams(params));
}

function cleanAnalyticsParams(params: AnalyticsParams): AnalyticsParams {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined));
}

function hasGoogleAnalyticsScript(measurementId: string): boolean {
  const expectedUrl = googleAnalyticsScriptUrl(measurementId);
  return Array.from(document.scripts).some((script) => script.src === expectedUrl);
}

function hasConfigCommand(measurementId: string): boolean {
  return Boolean(window.dataLayer?.some((command) => dataLayerValue(command, 0) === "config" && dataLayerValue(command, 1) === measurementId));
}

function gtagShim(..._command: GtagCommand): void {
  window.dataLayer?.push(arguments);
}

function dataLayerValue(entry: DataLayerEntry, index: number): unknown {
  return (entry as { [key: number]: unknown })[index];
}
